/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from "react";
import parse from "html-react-parser";
import { ImageOff } from "lucide-react";
import getMediaUrl, { getDefaultProductImage } from "../tools/utils.jsx";

function stripSvgSize(svgString) {
    return String(svgString || "")
        .replace(/width="[^"]*"/, "")
        .replace(/height="[^"]*"/, "");
}

export default function CommerceItemImage({
    item,
    product,
    customKeyring,
    alt = "Producto",
    className = "w-16 h-16",
    show3dOnHover = false,
}) {
    const [imageFailed, setImageFailed] = useState(false);

    const keyring = customKeyring
        || item?.custom_order_request_detail?.customized_keyring
        || item?.customized_keyring_detail
        || item?.customized_keyring;

    const productData = product || item?.product_detail || item?.product;
    const thumbnail = keyring?.path_thumbnail || keyring?.thumbnail || item?.thumbnail;
    const thumbnail3d = keyring?.path_thumbnail_3d || keyring?.thumbnail3d || item?.thumbnail3d;
    const productImage = getDefaultProductImage(productData);
    const imageSrc = thumbnail || productImage;
    const image3dSrc = thumbnail3d ? getMediaUrl(thumbnail3d) : '';
    const svg = keyring?.svg_2d || item?.svg_2d;

    useEffect(() => {
        setImageFailed(false);
    }, [imageSrc]);

    const parsedSvg = useMemo(() => {
        if (!svg) return null;
        return parse(`<?xml version="1.0" encoding="UTF-8" standalone="no"?>${stripSvgSize(svg)}`);
    }, [svg]);

    const frameClass = `${className} rounded-lg bg-base-200 border border-base-300 flex-shrink-0 overflow-hidden`;

    if (imageSrc && !imageFailed) {
        return (
            <div className={`${frameClass} relative group`}>
                <img
                    src={getMediaUrl(imageSrc)}
                    alt={alt}
                    loading="lazy"
                    decoding="async"
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${show3dOnHover && image3dSrc ? 'group-hover:opacity-0' : ''}`}
                    onError={() => setImageFailed(true)}
                />
                {show3dOnHover && image3dSrc && (
                    <img
                        src={image3dSrc}
                        alt={`${alt} en 3D`}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                    />
                )}
            </div>
        );
    }

    if (parsedSvg) {
        return (
            <div className={`${frameClass} svg-checker relative group flex items-center justify-center p-2`}>
                <div className="w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg]:object-contain">
                    {parsedSvg}
                </div>
                {show3dOnHover && image3dSrc && (
                    <img
                        src={image3dSrc}
                        alt={`${alt} en 3D`}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                    />
                )}
            </div>
        );
    }

    if (image3dSrc) {
        return (
            <img
                src={image3dSrc}
                alt={`${alt} en 3D`}
                loading="lazy"
                decoding="async"
                className={`${frameClass} object-cover`}
            />
        );
    }

    return (
        <div className={`${frameClass} flex items-center justify-center text-base-content/35`}>
            <ImageOff className="w-5 h-5" />
        </div>
    );
}
